import React, { useState, useEffect } from 'react';
import ReportFilterBar from '../components/ReportFilterBar';
import ReportTable from '../components/ReportTable';
import ReportDownloadButtons from '../components/ReportDownloadButtons';
import PrimaryDealsModal from '../components/PrimaryDealsModal';
import { API_BASE_URL } from '../services/api';

const GsecReportPage = () => {
  // State for filters, results, pagination, loading, and errors
  const [filters, setFilters] = useState({});
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPortfolioBalance, setTotalPortfolioBalance] = useState(null);
  const [portfolioRunningDate, setPortfolioRunningDate] = useState('');
  const [selectedBalanceRow, setSelectedBalanceRow] = useState(null);

  // Portfolio Running Date - the system's current EOD-processed business date.
  // Used both as the report heading and as the default "as at" filter so the
  // report opens already populated, instead of forcing a manual date pick.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/system-day`);
        if (!res.ok) return;
        const json = await res.json();
        const day = json.system_day ? String(json.system_day).split('T')[0] : '';
        if (day) {
          setPortfolioRunningDate(day);
          setFilters((f) => (f.asAtDate ? f : { ...f, asAtDate: day, dateFrom: f.dateFrom || day, dateTo: f.dateTo || day }));
        }
      } catch (e) {
        // graceful fallback - heading just won't show a date
      }
    })();
  }, []);

  // ISIN dropdown state
  const [isinOptions, setIsinOptions] = useState([]);
  const [isinLoading, setIsinLoading] = useState(true);
  const [isinError, setIsinError] = useState('');

  useEffect(() => {
    async function fetchIsins() {
      setIsinLoading(true);
      setIsinError('');
      try {
        const res = await fetch(`${API_BASE_URL}/isin-master`);
        if (!res.ok) throw new Error('Failed to fetch ISINs');
        const json = await res.json();
        const data = json.data || [];
        setIsinOptions(data.map(i => ({
          value: i.isin_number || i.isin,
          label: `${i.isin_number || i.isin}${i.description ? ' - ' + i.description : ''}`
        })));
      } catch (e) {
        setIsinError(e.message || 'Error fetching ISINs');
      } finally {
        setIsinLoading(false);
      }
    }
    fetchIsins();
  }, []);

  // Debug log for troubleshooting blank screen
  console.log('Rendering GsecReportPage', { filters, results, pagination, loading, error, isinOptions, isinLoading, isinError });

  // Handler for filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Handler for generating the report (calls backend API)
  const handleGenerateReport = async (filtersArg) => {
    const usedFilters = filtersArg || filters;
    if (usedFilters.view === 'transactions') {
      if (!usedFilters.dateFrom || !usedFilters.dateTo) {
        setError('Please select Date From and Date To before generating the transactions report.');
        return;
      }
    } else if (!usedFilters.asAtDate && !usedFilters.isin) {
      setError('Please select an "As at Date" or an ISIN before generating the report.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        ...usedFilters,
        page: pagination.page,
        pageSize: pagination.pageSize
      });
      const res = await fetch(`${API_BASE_URL}/reports/gsec?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch report');
      const json = await res.json();
      setResults(json.data || []);
      setPagination(p => ({ ...p, total: json.total || 0 }));
      setTotalPortfolioBalance(json.totalPortfolioBalance || null);
    } catch (e) {
      setError(e.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  // Handler for page change
  const handlePageChange = async (newPagination) => {
    setPagination(newPagination);
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        ...filters,
        page: newPagination.page,
        pageSize: newPagination.pageSize
      });
      const res = await fetch(`${API_BASE_URL}/reports/gsec?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch report');
      const json = await res.json();
      setResults(json.data || []);
      setPagination(p => ({ ...newPagination, total: json.total || 0 }));
      setTotalPortfolioBalance(json.totalPortfolioBalance || null);
    } catch (e) {
      setError(e.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  // Handler for download
  const handleDownload = async (format) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        ...filters,
        format
        // Remove pagination for downloads to get all data
      });
      const res = await fetch(`${API_BASE_URL}/reports/gsec?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to download report');
      const blob = await res.blob();
      // Create a link to download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filters.view === 'transactions' ? 't_bond_transactions_report' : 't_bond_report'}.${format === 'excel' ? 'xlsx' : format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Error downloading report');
    } finally {
      setLoading(false);
    }
  };

  if (error) return <div style={{ color: 'red', padding: 24 }}>Error: {error}</div>;

  return (
    <div className="gsec-report-page ui-page">
      <h2>{filters.view === 'transactions' ? 'T Bond Transactions Report' : 'T Bond Product Report'}</h2>
      {portfolioRunningDate && (
        <div style={{ color: '#495057', marginBottom: 12, fontWeight: 600 }}>
          Portfolio Running Date: {portfolioRunningDate}
        </div>
      )}
      <ReportFilterBar
        filters={filters}
        onChange={handleFilterChange}
        onGenerate={handleGenerateReport}
        isinOptions={isinOptions}
        showReportView
      />
      {isinLoading && <div style={{ color: '#555', marginBottom: 12 }}>Loading ISINs...</div>}
      {isinError && <div style={{ color: 'red', marginBottom: 12 }}>Error loading ISINs: {isinError}</div>}
      <ReportDownloadButtons onDownload={handleDownload} disabled={loading || results.length === 0} />
      <ReportTable
        data={results}
        pagination={pagination}
        loading={loading}
        error={error}
        onPageChange={handlePageChange}
        onFaceValueClick={setSelectedBalanceRow}
        reportType={filters.view === 'transactions' ? 'gsec-transactions' : 'gsec'}
      />
      {selectedBalanceRow && (
        <PrimaryDealsModal row={selectedBalanceRow} onClose={() => setSelectedBalanceRow(null)} />
      )}
      
      {/* Total Portfolio Balance Summary */}
      {filters.view !== 'transactions' && totalPortfolioBalance && (
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          borderRadius: '5px',
          textAlign: 'right'
        }}>
          <h5 style={{ margin: '0 0 10px 0', color: '#495057' }}>
            Total Portfolio Balance: 
            <span style={{ 
              marginLeft: '10px', 
              fontSize: '1.2em', 
              fontWeight: 'bold', 
              color: '#007bff' 
            }}>
              {totalPortfolioBalance}
            </span>
          </h5>
          <small style={{ color: '#6c757d' }}>
            Sum of all ISIN balances for portfolio: {filters.portfolio}
          </small>
        </div>
      )}
      
    </div>
  );
}

export default GsecReportPage;
